/**
 * LYT Communications - Sub-Contractor Dashboard
 * Version: 1.0.0
 * Created: 2026-02-23
 *
 * Restricted dashboard for sub-contractors working under a parent contractor.
 * Shows only assigned segments, allows progress reporting and photo upload.
 * NO billing, NO rates, NO other contractor data visible.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LogOut, Briefcase, Activity, MapPin, CheckCircle, AlertTriangle, Shield, Map, Menu, X, ChevronRight, Loader, Truck, ShieldAlert, User, Users, MoreHorizontal, Sun, Moon, Home, Square } from 'lucide-react';
import { loadFullProject, getContractorSegments, loadAssignments, getSubAssignments, subAcceptAssignment, declineAssignment, updateSegmentField, detectContractorPhases, getPhaseStatusField, filterSegmentsByPhase, loadMapNotes } from '../services/mapService';
import AssignmentManager from '../components/Workflow/AssignmentManager';
import { colors } from '../config/constants';
import { fetchWithRedirect } from '../utils/fetchHelpers';
import NotificationBell from '../components/NotificationBell';
import { GoogleMap, useJsApiLoader, Polyline, DrawingManager } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBS, STATUS_COLORS } from '../config/mapConfig';
import MapActionBar, { MapStatsBar, MapFloatingButton, getMapStatusColor, QuickStatusButtons } from '../components/Map/MapActionBar';
import MapNotesLayer from '../components/Map/MapNotesLayer';
import MyLocationMarker, { GpsToggleButton } from '../components/Map/MyLocationMarker';

// API URLs
const GATEWAY_URL = 'https://script.google.com/macros/s/AKfycbyFWHLgFOglJ75Y6AGnyme0P00OjFgE_-qrDN9m0spn4HCgcyBpjvMopsB1_l9MDjIctQ/exec';
const GATEWAY_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';
const OPERATIONS_SHEET_ID = '1VciM5TqHC5neB7JzpcFkX0qyoyzjBvIS0fKkOXQqnrc';

const submitToSheet = async (sheetName, rowData) => {
  try {
    const text = await fetchWithRedirect(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        secret: GATEWAY_SECRET,
        action: 'sheetsAppend',
        params: {
          spreadsheetId: OPERATIONS_SHEET_ID,
          range: `${sheetName}!A:Z`,
          values: [rowData]
        }
      })
    });
    const result = JSON.parse(text);
    return result.success;
  } catch (err) {
    console.error('Submit error:', err);
    return false;
  }
};

const SubDashboard = ({ setCurrentPage, loggedInUser, setLoggedInUser, darkMode }) => {
  const accentPrimary = darkMode ? '#667eea' : '#00b4d8';
  const accentSecondary = darkMode ? '#ff6b35' : '#28a745';
  const accentError = darkMode ? '#ff6b6b' : '#e85a4f';

  const bgColor = darkMode ? colors.dark : '#f8fafc';
  const cardBg = darkMode ? colors.darkLight : '#ffffff';
  const textColor = darkMode ? '#ffffff' : colors.dark;
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';

  const [activeTab, setActiveTabRaw] = useState(() => {
    try { return sessionStorage.getItem('sub-tab') || 'dashboard'; } catch { return 'dashboard'; }
  });
  const setActiveTab = useCallback((tab) => {
    setActiveTabRaw(prev => {
      if (prev === tab) return prev;
      try { sessionStorage.setItem('sub-tab', tab); } catch { /* noop */ }
      const currentState = window.history.state || {};
      window.history.pushState({ ...currentState, subTab: tab }, '');
      return tab;
    });
  }, []);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ show: false, success: false, message: '' });

  const [mySegments, setMySegments] = useState([]);
  const [segmentsLoading, setSegmentsLoading] = useState(true);
  const [segmentsError, setSegmentsError] = useState(false);

  // Phase detection: which work phases this sub-contractor is assigned to
  const [myPhases, setMyPhases] = useState(['boring']);

  const [myAssignments, setMyAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  // Production map state
  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBS });
  const [statusUpdating, setStatusUpdating] = useState(false);
  const prodMapRef = useRef(null);

  // Unified selection — tap always selects, no mode toggle
  const [selectedSegIds, setSelectedSegIds] = useState(new Set());
  const [drawRectMode, setDrawRectMode] = useState(false);
  const [quickStatusOn, setQuickStatusOn] = useState(() => { try { const v = localStorage.getItem('lyt-quick-status'); return v === null ? true : v === 'true'; } catch { return true; } });

  const [mapNotes, setMapNotes] = useState([]);
  const [showNotes, setShowNotes] = useState(true);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [gpsOn, setGpsOn] = useState(false);

  const [productionForm, setProductionForm] = useState({
    project: '',
    fiberFootage: '',
    splices: '',
    drops: '',
    poles: '',
    hddFootage: '',
    notes: ''
  });

  // Browser back/forward for tab navigation
  useEffect(() => {
    const onPopState = (e) => {
      if (e.state && e.state.subTab && e.state.page === 'sub-dashboard') {
        setActiveTabRaw(e.state.subTab);
        try { sessionStorage.setItem('sub-tab', e.state.subTab); } catch { /* noop */ }
      }
    };
    window.addEventListener('popstate', onPopState);
    if (!window.history.state?.subTab) {
      window.history.replaceState({ ...window.history.state, subTab: activeTab }, '');
    }
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load assigned segments — subs see segments assigned to their parent contractor's company
  useEffect(() => {
    const loadMySegments = async () => {
      try {
        setSegmentsLoading(true);
        setSegmentsError(false);
        const data = await loadFullProject(null);
        if (data && data.segments) {
          const companyName = loggedInUser?.company || loggedInUser?.name || '';
          const filtered = getContractorSegments(data.segments, companyName);
          // Detect phases and filter segments to only this contractor's assigned phases
          const phases = detectContractorPhases(filtered, companyName);
          setMyPhases(phases);
          setMySegments(filterSegmentsByPhase(filtered, phases, companyName));

          // Load map notes
          try {
            const notesPid = filtered[0]?.project_id || loggedInUser?.project_id || 'BASTROP';
            const notesData = await loadMapNotes(notesPid);
            setMapNotes(notesData || []);
          } catch (e) { /* noop */ }
        }
      } catch (err) {
        console.error('[SubDashboard] Segment load error:', err);
        setSegmentsError(true);
      } finally {
        setSegmentsLoading(false);
      }
    };
    if (loggedInUser) {
      loadMySegments();
    }
  }, [loggedInUser]);

  const showNotification = (success, message) => {
    setSubmitStatus({ show: true, success, message });
    setTimeout(() => setSubmitStatus({ show: false, success: false, message: '' }), 3000);
  };

  const handleLogout = () => {
    setLoggedInUser(null);
    setCurrentPage('home');
  };

  const handleProductionSubmit = async () => {
    if (!productionForm.project) {
      showNotification(false, 'Please select a project');
      return;
    }
    setLoading(true);
    const success = await submitToSheet('Contractor Production', [
      new Date().toISOString(),
      loggedInUser?.email || '',
      loggedInUser?.name || '',
      productionForm.project,
      productionForm.fiberFootage || '0',
      productionForm.splices || '0',
      productionForm.drops || '0',
      productionForm.poles || '0',
      productionForm.hddFootage || '0',
      productionForm.notes,
      'Submitted',
      loggedInUser?.parent_email || '',
    ]);
    setLoading(false);
    if (success) {
      showNotification(true, 'Production log submitted!');
      setProductionForm({ project: '', fiberFootage: '', splices: '', drops: '', poles: '', hddFootage: '', notes: '' });
    } else {
      showNotification(false, 'Failed to submit. Please try again.');
    }
  };

  // Load assignments for this sub
  const loadMyAssignments = async () => {
    setAssignmentsLoading(true);
    try {
      const all = await loadAssignments(null);
      const mine = getSubAssignments(all, loggedInUser?.email);
      setMyAssignments(mine);
    } catch (err) {
      console.error('[SubDashboard] Assignments load error:', err);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Briefcase },
    { id: 'assignments', label: 'Assignments', icon: Users },
    { id: 'profile', label: 'My Profile', icon: User, external: 'profile' },
    { id: 'project-map', label: 'Project Map', icon: Map, external: 'project-map' },
    { id: 'production', label: 'Daily Production', icon: Activity },
    { id: 'incidents', label: 'Incident Reports', icon: ShieldAlert },
  ];

  const bottomTabs = [
    { id: 'dashboard', label: 'Home', icon: Home },
    { id: 'production', label: 'Production', icon: Activity },
    { id: 'assignments', label: 'Tasks', icon: Users },
    { id: 'more', label: 'More', icon: MoreHorizontal },
  ];

  // Phase-aware status field
  const statusField = getPhaseStatusField(myPhases);

  const segStats = {
    total: mySegments.length,
    completed: mySegments.filter(s => s[statusField] === 'QC Approved' || s[statusField] === 'Complete').length,
    inProgress: mySegments.filter(s => s[statusField] === 'In Progress').length,
    notStarted: mySegments.filter(s => s[statusField] === 'Not Started').length,
    issues: mySegments.filter(s => s[statusField] === 'Issue').length,
    totalFootage: mySegments.reduce((sum, s) => sum + (parseFloat(s.footage) || 0), 0),
  };

  const formInputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
    backgroundColor: '#ffffff',
    color: '#1f2937',
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const renderDashboard = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>
          Welcome, {loggedInUser?.name?.split(' ')[0] || 'Sub'}!
        </h2>
        <p style={{ color: mutedColor }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        {loggedInUser?.company && (
          <p style={{ color: mutedColor, fontSize: '0.85rem', marginTop: 4 }}>Company: {loggedInUser.company}</p>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[
          { label: 'Assigned Segments', value: segmentsLoading ? '...' : String(segStats.total), subtext: `${segStats.totalFootage.toLocaleString()} LF total`, icon: MapPin, color: accentPrimary },
          { label: 'Completed', value: segmentsLoading ? '...' : String(segStats.completed), subtext: `${segStats.inProgress} in progress`, icon: CheckCircle, color: accentSecondary },
          { label: 'Issues', value: segmentsLoading ? '...' : String(segStats.issues), subtext: segStats.issues > 0 ? 'Needs attention' : 'All clear', icon: segStats.issues > 0 ? AlertTriangle : Shield, color: segStats.issues > 0 ? accentError : accentSecondary },
        ].map((stat, idx) => (
          <div key={idx} style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${stat.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <stat.icon size={20} color={stat.color} />
              </div>
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: textColor }}>{stat.value}</div>
            <div style={{ fontSize: '0.85rem', color: mutedColor }}>{stat.label}</div>
            <div style={{ fontSize: '0.75rem', color: mutedColor }}>{stat.subtext}</div>
          </div>
        ))}
      </div>

      {/* Segment List */}
      <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <h3 style={{ color: textColor, marginBottom: '16px', fontSize: '1.1rem' }}>My Assigned Segments</h3>
        {segmentsLoading ? (
          <div style={{ textAlign: 'center', padding: '24px', color: mutedColor }}>
            <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} /> Loading...
          </div>
        ) : segmentsError ? (
          <div style={{ textAlign: 'center', padding: '24px', color: accentError }}>
            <AlertTriangle size={24} style={{ marginBottom: '8px' }} />
            <div>Failed to load segments. Check your connection and try again.</div>
          </div>
        ) : mySegments.length === 0 ? (
          <p style={{ color: mutedColor, textAlign: 'center', padding: '24px' }}>No segments assigned yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
                  {['Segment', 'Street', 'Footage', 'Boring', 'Pulling'].map(h => (
                    <th key={h} style={{ padding: '10px', textAlign: 'left', color: mutedColor, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mySegments.slice(0, 20).map((seg, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${darkMode ? '#374151' : '#f1f5f9'}` }}>
                    <td style={{ padding: '10px', color: textColor, fontWeight: 600 }}>{seg.segment_id}</td>
                    <td style={{ padding: '10px', color: mutedColor }}>{seg.street_name || '—'}</td>
                    <td style={{ padding: '10px', color: textColor }}>{seg.footage || '—'} LF</td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        padding: '3px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600,
                        backgroundColor: seg[statusField] === 'QC Approved' ? '#4CAF5020' : seg[statusField] === 'In Progress' ? '#FFB80020' : '#FF444420',
                        color: seg[statusField] === 'QC Approved' ? '#4CAF50' : seg[statusField] === 'In Progress' ? '#FFB800' : '#FF4444',
                      }}>{seg[statusField] || 'Not Started'}</span>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        padding: '3px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600,
                        backgroundColor: seg.pulling_status === 'QC Approved' ? '#4CAF5020' : seg.pulling_status === 'In Progress' ? '#FFB80020' : '#9E9E9E20',
                        color: seg.pulling_status === 'QC Approved' ? '#4CAF50' : seg.pulling_status === 'In Progress' ? '#FFB800' : '#9E9E9E',
                      }}>{seg.pulling_status || 'Not Started'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {mySegments.length > 20 && (
              <p style={{ color: mutedColor, fontSize: '0.8rem', textAlign: 'center', marginTop: 8 }}>
                Showing 20 of {mySegments.length} segments
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Unified status update handler — works for single or batch
  const handleStatusUpdate = async (newStatus) => {
    if (selectedSegIds.size === 0) return;
    setStatusUpdating(true);
    const now = new Date().toISOString();
    const phasePrefix = statusField.replace('_status', '');
    const tsField = newStatus === 'Complete' ? `${phasePrefix}_completed` : newStatus === 'In Progress' ? `${phasePrefix}_started` : null;

    let successCount = 0;
    const ids = [...selectedSegIds];
    for (const segId of ids) {
      const ok = await updateSegmentField(segId, statusField, newStatus);
      if (ok) {
        if (tsField) updateSegmentField(segId, tsField, now);
        successCount++;
      }
    }

    const updates = { [statusField]: newStatus };
    if (tsField) updates[tsField] = now;
    setMySegments(prev => prev.map(s =>
      selectedSegIds.has(s.segment_id) ? { ...s, ...updates } : s
    ));

    setSelectedSegIds(new Set());
    setStatusUpdating(false);
    showNotification(true, `${successCount} segment${successCount !== 1 ? 's' : ''} → ${newStatus}`);
  };

  // Rectangle select handler
  const onRectangleComplete = (rect) => {
    const bounds = rect.getBounds();
    if (!bounds) { rect.setMap(null); return; }
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const newSelected = new Set(selectedSegIds);
    const mappableSegs = mySegments.filter(s => {
      const slat = parseFloat(s.gps_start?.lat ?? s.gps_start_lat);
      const slng = parseFloat(s.gps_start?.lng ?? s.gps_start_lng);
      return !isNaN(slat) && !isNaN(slng) && (slat !== 0 || slng !== 0);
    });
    mappableSegs.forEach(s => {
      const slat = parseFloat(s.gps_start?.lat ?? s.gps_start_lat);
      const slng = parseFloat(s.gps_start?.lng ?? s.gps_start_lng);
      const elat = parseFloat(s.gps_end?.lat ?? s.gps_end_lat);
      const elng = parseFloat(s.gps_end?.lng ?? s.gps_end_lng);
      const inBounds = (lat, lng) => lat >= sw.lat() && lat <= ne.lat() && lng >= sw.lng() && lng <= ne.lng();
      if (inBounds(slat, slng) || inBounds(elat, elng)) {
        newSelected.add(s.segment_id);
      }
    });
    setSelectedSegIds(newSelected);
    setDrawRectMode(false);
    rect.setMap(null);
  };

  const getProdColor = getMapStatusColor;

  const renderProduction = () => {
    const mappableSegs = mySegments.filter(s => {
      const slat = parseFloat(s.gps_start?.lat ?? s.gps_start_lat);
      const slng = parseFloat(s.gps_start?.lng ?? s.gps_start_lng);
      const elat = parseFloat(s.gps_end?.lat ?? s.gps_end_lat);
      const elng = parseFloat(s.gps_end?.lng ?? s.gps_end_lng);
      return !isNaN(slat) && !isNaN(slng) && !isNaN(elat) && !isNaN(elng) && (slat !== 0 || slng !== 0);
    });

    const prodStats = {
      total: mySegments.length,
      complete: mySegments.filter(s => s[statusField] === 'Complete' || s[statusField] === 'QC Approved').length,
      inProgress: mySegments.filter(s => s[statusField] === 'In Progress').length,
      issues: mySegments.filter(s => s[statusField] === 'Issue').length,
    };

    if (segmentsLoading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: mutedColor }}>
          <Loader size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '10px' }} /> Loading segments...
        </div>
      );
    }
    if (mySegments.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <MapPin size={64} color={mutedColor} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <h2 style={{ color: textColor, fontWeight: 600, marginBottom: '8px' }}>No segments assigned</h2>
          <p style={{ color: mutedColor }}>Segments will appear here once your contractor assigns work to you.</p>
        </div>
      );
    }
    if (!mapsLoaded) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: mutedColor }}>
          <Loader size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '10px' }} /> Loading map...
        </div>
      );
    }

    const onMapLoad = (map) => {
      prodMapRef.current = map;
      if (mappableSegs.length === 0) return;
      const bounds = new window.google.maps.LatLngBounds();
      mappableSegs.forEach(s => {
        bounds.extend({ lat: parseFloat(s.gps_start?.lat ?? s.gps_start_lat), lng: parseFloat(s.gps_start?.lng ?? s.gps_start_lng) });
        bounds.extend({ lat: parseFloat(s.gps_end?.lat ?? s.gps_end_lat), lng: parseFloat(s.gps_end?.lng ?? s.gps_end_lng) });
      });
      map.fitBounds(bounds, 40);
    };

    const mapHeight = isMobile ? 'calc(100vh - 112px)' : 'calc(100vh - 80px)';

    // Resolve single-selected segment object for the action bar
    const singleSelectedSeg = selectedSegIds.size === 1 ? mySegments.find(s => selectedSegIds.has(s.segment_id)) : null;

    return (
      <div style={{ position: 'relative', height: mapHeight, margin: isMobile ? '-16px' : '-32px', marginTop: isMobile ? '-8px' : '-32px' }}>
        {/* Shared stats bar */}
        <MapStatsBar
          darkMode={darkMode}
          total={prodStats.total}
          done={prodStats.complete}
          active={prodStats.inProgress}
          issues={prodStats.issues}
          selectedCount={selectedSegIds.size}
          style={{ top: isMobile ? '8px' : '12px' }}
        />

        {/* Draw button — always visible */}
        <div style={{
          position: 'absolute', top: isMobile ? '8px' : '12px', right: '12px', zIndex: 10,
        }}>
          <MapFloatingButton
            darkMode={darkMode}
            active={drawRectMode}
            onClick={() => setDrawRectMode(!drawRectMode)}
            icon={<Square size={16} />}
            label="Draw"
          />
          <div style={{ marginTop: '8px' }}>
            <MapFloatingButton
              darkMode={darkMode}
              active={quickStatusOn}
              onClick={() => {
                setQuickStatusOn(prev => {
                  const next = !prev;
                  try { localStorage.setItem('lyt-quick-status', String(next)); } catch {}
                  return next;
                });
              }}
              icon={<CheckCircle size={16} />}
              label="Quick"
              style={{ backgroundColor: quickStatusOn ? '#4CAF50' : undefined, color: quickStatusOn ? '#fff' : undefined }}
            />
          </div>
          <div style={{ marginTop: '8px' }}>
            <MapFloatingButton
              darkMode={darkMode}
              active={isAddingNote}
              onClick={() => setIsAddingNote(prev => !prev)}
              icon={<MapPin size={16} />}
              label="Note"
              style={{ backgroundColor: isAddingNote ? '#FF9800' : undefined, color: isAddingNote ? '#fff' : undefined }}
            />
          </div>
        </div>

        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={prodMapRef.current ? undefined : (mappableSegs.length > 0 ? {
            lat: parseFloat(mappableSegs[0].gps_start?.lat ?? mappableSegs[0].gps_start_lat),
            lng: parseFloat(mappableSegs[0].gps_start?.lng ?? mappableSegs[0].gps_start_lng),
          } : { lat: 30.1, lng: -97.3 })}
          zoom={prodMapRef.current ? undefined : 14}
          onLoad={onMapLoad}
          mapTypeId={(() => { try { return localStorage.getItem('lyt-map-type') || 'roadmap'; } catch { return 'roadmap'; } })()}
          options={{
            streetViewControl: false, mapTypeControl: false, fullscreenControl: false,
            zoomControl: !isMobile,
            gestureHandling: drawRectMode ? 'none' : 'greedy',
          }}
          onClick={(e) => { if (isAddingNote && MapNotesLayer.handleMapClick) MapNotesLayer.handleMapClick(e); }}
        >
          {mappableSegs.map(seg => {
            const isSelected = selectedSegIds.has(seg.segment_id);
            const path = [
              { lat: parseFloat(seg.gps_start?.lat ?? seg.gps_start_lat), lng: parseFloat(seg.gps_start?.lng ?? seg.gps_start_lng) },
              { lat: parseFloat(seg.gps_end?.lat ?? seg.gps_end_lat), lng: parseFloat(seg.gps_end?.lng ?? seg.gps_end_lng) },
            ];
            return (
              <Polyline key={seg.segment_id} path={path} options={{
                strokeColor: isSelected ? '#FFD700' : getProdColor(seg[statusField]),
                strokeWeight: isSelected ? 8 : 5,
                strokeOpacity: isSelected ? 1 : 0.85,
                zIndex: isSelected ? 10 : 1,
              }} onClick={() => {
                setSelectedSegIds(prev => {
                  const next = new Set(prev);
                  if (next.has(seg.segment_id)) next.delete(seg.segment_id);
                  else next.add(seg.segment_id);
                  return next;
                });
              }} />
            );
          })}

          {/* Drawing manager for rectangle select */}
          {drawRectMode && (
            <DrawingManager
              drawingMode="rectangle"
              onRectangleComplete={onRectangleComplete}
              options={{
                drawingControl: false,
                rectangleOptions: {
                  fillColor: '#FFD700', fillOpacity: 0.15,
                  strokeColor: '#FFD700', strokeWeight: 2,
                  editable: false, draggable: false,
                },
              }}
            />
          )}

          {/* Map Notes */}
          {showNotes && (
            <MapNotesLayer
              notes={mapNotes}
              darkMode={darkMode}
              loggedInUser={loggedInUser}
              projectId={mySegments[0]?.project_id || loggedInUser?.project_id || 'BASTROP'}
              section={null}
              onNotesChanged={async () => {
                try {
                  const notesData = await loadMapNotes(mySegments[0]?.project_id || loggedInUser?.project_id || 'BASTROP');
                  setMapNotes(notesData || []);
                } catch (e) { /* noop */ }
              }}
              isAddingNote={isAddingNote}
              onAddNoteToggle={setIsAddingNote}
              segments={mappableSegs || []}
              devices={[]}
              isAdmin={false}
              isMobile={isMobile}
            />
          )}
          {/* GPS blue dot */}
          {gpsOn && loggedInUser && (
            <MyLocationMarker
              loggedInUser={loggedInUser}
              segments={mappableSegs || []}
              projectId={mySegments[0]?.project_id || loggedInUser?.project_id || 'BASTROP'}
            />
          )}
        </GoogleMap>

        {loggedInUser && <GpsToggleButton darkMode={darkMode} mapRef={prodMapRef.current} gpsOn={gpsOn} onToggle={setGpsOn} />}

        {/* Quick Status floating buttons */}
        <QuickStatusButtons
          darkMode={darkMode}
          visible={quickStatusOn}
          selectedCount={selectedSegIds.size}
          onStatusUpdate={handleStatusUpdate}
          updating={statusUpdating}
        />

        {/* Unified MapActionBar — single or batch */}
        <MapActionBar
          darkMode={darkMode}
          selectedSegment={singleSelectedSeg}
          selectedCount={selectedSegIds.size > 1 ? selectedSegIds.size : 0}
          statusField={statusField}
          onStatusUpdate={handleStatusUpdate}
          onClear={() => setSelectedSegIds(new Set())}
          updating={statusUpdating}
        />
      </div>
    );
  };

  const renderIncidents = () => (
    <div style={{ maxWidth: '600px' }}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: textColor, marginBottom: '24px' }}>Report an Incident</h2>
      <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <p style={{ color: mutedColor, marginBottom: '16px' }}>
          Report safety incidents, near-misses, or utility strikes immediately.
        </p>
        <button
          onClick={() => setCurrentPage('project-map')}
          style={{
            width: '100%', padding: '14px', backgroundColor: accentError, color: '#fff',
            border: 'none', borderRadius: '8px', cursor: 'pointer',
            fontWeight: '600', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          <ShieldAlert size={18} /> Open Project Map to Report
        </button>
      </div>
    </div>
  );

  const renderAssignments = () => {
    if (myAssignments.length === 0 && !assignmentsLoading) loadMyAssignments();
    return (
      <div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: textColor, marginBottom: '24px' }}>My Assignments</h2>
        <p style={{ color: mutedColor, marginBottom: '16px' }}>Work assigned to you by your contractor.</p>
        {assignmentsLoading ? (
          <div style={{ textAlign: 'center', padding: '24px', color: mutedColor }}>
            <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} /> Loading assignments...
          </div>
        ) : (
          <AssignmentManager
            assignments={myAssignments}
            subs={[]}
            darkMode={darkMode}
            user={loggedInUser}
            onSubAccept={async (id) => {
              await subAcceptAssignment(id);
              await loadMyAssignments();
            }}
            onDecline={async (id, reason) => {
              await declineAssignment(id, reason);
              await loadMyAssignments();
            }}
          />
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'assignments': return renderAssignments();
      case 'production': return renderProduction();
      case 'incidents': return renderIncidents();
      default: return renderDashboard();
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, display: 'flex', WebkitOverflowScrolling: 'touch', overflowX: 'hidden' }}>
      {submitStatus.show && (
        <div style={{ position: 'fixed', top: isMobile ? '70px' : '20px', right: '20px', left: isMobile ? '20px' : 'auto', padding: '16px 24px', backgroundColor: submitStatus.success ? accentSecondary : accentError, color: '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1100, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {submitStatus.success ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
          {submitStatus.message}
        </div>
      )}

      {isMobile && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '56px', backgroundColor: cardBg, borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', zIndex: 1000 }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer', padding: '8px', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>
            <span style={{ color: darkMode ? '#e6c4d9' : '#0a3a7d' }}>ly</span>
            <span style={{ color: darkMode ? '#e6c4d9' : '#2ec7c0' }}>t</span>
            <span style={{ color: mutedColor, fontSize: '0.85rem', marginLeft: '6px' }}>Sub</span>
          </div>
          <NotificationBell darkMode={darkMode} user={loggedInUser} setCurrentPage={setCurrentPage} />
        </div>
      )}

      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1001 }} />
      )}

      <div style={{ width: '260px', backgroundColor: cardBg, borderRight: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`, display: 'flex', flexDirection: 'column', position: 'fixed', height: '100vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', zIndex: isMobile ? 1002 : 1, transform: isMobile ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)', transition: isMobile ? 'transform 0.3s ease' : 'none', top: 0, left: 0 }}>
        <div style={{ padding: '20px', borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
            <span style={{ color: darkMode ? '#e6c4d9' : '#0a3a7d' }}>ly</span>
            <span style={{ color: darkMode ? '#e6c4d9' : '#2ec7c0' }}>t</span>
            <span style={{ color: mutedColor, fontSize: '0.9rem', marginLeft: '8px' }}>Sub</span>
          </div>
          {isMobile && (
            <button onClick={() => setSidebarOpen(false)} aria-label="Close sidebar" style={{ background: 'none', border: 'none', color: mutedColor, cursor: 'pointer', padding: '8px', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={20} />
            </button>
          )}
        </div>

        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: `${accentSecondary}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentSecondary, fontWeight: '600' }}>
              {loggedInUser?.name?.charAt(0) || 'S'}
            </div>
            <div>
              <div style={{ fontWeight: '500', color: textColor }}>{loggedInUser?.name || 'Sub-Contractor'}</div>
              <div style={{ fontSize: '0.75rem', color: mutedColor }}>{loggedInUser?.email}</div>
              {loggedInUser?.company && <div style={{ fontSize: '0.7rem', color: mutedColor }}>{loggedInUser.company}</div>}
            </div>
          </div>
        </div>

        <div style={{ flex: 1, padding: '12px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {navItems.map((item) => (
            <button key={item.id} onClick={() => { if (item.external) setCurrentPage(item.external); else setActiveTab(item.id); if (isMobile) setSidebarOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: isMobile ? '14px 16px' : '12px 16px', marginBottom: '4px', backgroundColor: activeTab === item.id ? `${accentPrimary}15` : 'transparent', border: 'none', borderRadius: '8px', cursor: 'pointer', color: activeTab === item.id ? accentPrimary : textColor, textAlign: 'left', minHeight: isMobile ? '48px' : 'auto' }}>
              <item.icon size={18} />
              <span style={{ flex: 1, fontSize: '0.9rem' }}>{item.label}</span>
              {item.external && <ChevronRight size={14} color={mutedColor} />}
            </button>
          ))}
        </div>

        <div style={{ padding: '16px', borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
          <button onClick={handleLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: isMobile ? '14px' : '12px', backgroundColor: 'transparent', border: `1px solid ${accentError}`, borderRadius: '8px', color: accentError, cursor: 'pointer', fontSize: '0.9rem', minHeight: isMobile ? '48px' : 'auto' }}>
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </div>

      <div style={{ flex: 1, marginLeft: isMobile ? 0 : '260px', padding: isMobile ? '72px 16px 88px' : '32px', minHeight: '100vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {renderContent()}
      </div>

      {/* Mobile Bottom Tab Bar */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, height: '56px',
            backgroundColor: cardBg, borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-around',
            zIndex: 1000, paddingBottom: 'env(safe-area-inset-bottom)',
          }}>
            {bottomTabs.map(tab => {
              const isActive = tab.id === 'more' ? moreSheetOpen : activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => {
                  if (tab.id === 'more') { setMoreSheetOpen(!moreSheetOpen); }
                  else { setActiveTab(tab.id); setMoreSheetOpen(false); }
                }} style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0',
                  color: isActive ? accentPrimary : mutedColor,
                }}>
                  <tab.icon size={22} />
                  <span style={{ fontSize: '0.65rem', fontWeight: isActive ? 700 : 500 }}>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* More slide-up sheet */}
          {moreSheetOpen && (
            <>
              <div onClick={() => setMoreSheetOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1001 }} />
              <div style={{
                position: 'fixed', bottom: '56px', left: 0, right: 0, zIndex: 1002,
                backgroundColor: cardBg, borderRadius: '16px 16px 0 0',
                padding: '20px', paddingBottom: '24px',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
              }}>
                <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)', margin: '0 auto 16px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  {navItems.filter(n => !n.external && !['dashboard', 'production', 'assignments'].includes(n.id)).map(item => (
                    <button key={item.id} onClick={() => { setActiveTab(item.id); setMoreSheetOpen(false); }} style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                      padding: '16px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                      background: activeTab === item.id ? `${accentPrimary}12` : 'transparent',
                      color: activeTab === item.id ? accentPrimary : textColor,
                    }}>
                      <item.icon size={22} />
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{item.label}</span>
                    </button>
                  ))}
                  <button onClick={() => { setMoreSheetOpen(false); handleLogout(); }} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                    padding: '16px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                    background: 'transparent', color: accentError,
                  }}>
                    <LogOut size={22} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Sign Out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SubDashboard;
